import { getCities } from '../../../api/authApi'
import { getStudiosSuggestionsByIds } from '../../../api/studiosApi'
import { getTagsByContext } from '../../../api/tagsApi'
import config from '../../../config'
import { MAX_QS_STRINGIFY_LENGTH } from '../../../helpers/constant'
import { getDateTime } from '../../../helpers/utils'
import AuthPermission from '../../../high-order-components/auth-permission'
import translate from '../../../i18n'
import {
  ICorporateClientCredential,
  ICorporateDetailResponse,
} from '../../../pages/corporates-container/share-type'
import { ICity } from '../../../pages/prelaunch-organization-forms-container/share-types'
import { TaggingContextEnums } from '../../../pages/tags-container/constant'
import { ITag } from '../../../pages/tags-container/share-type'
import { PermissionsEnum } from '../../../share-types'
import YesOrNoTag from '../../common/YesOrNoTag'
import styles from './corporate-descriptions.module.less'
import {
  CloudUploadOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  SolutionOutlined,
  ToolOutlined,
} from '@ant-design/icons'
import { Button, Descriptions, Dropdown, Modal, Space, Tag } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

const { guavapassUrl } = config

const { confirm } = Modal

interface Iprops {
  corporate: ICorporateDetailResponse
  corporateClientCredential?: ICorporateClientCredential
  onEditCorporateClientCredential: () => void
  onDeleteCorporateClientCredential: () => void
  onAdjustmentCredits: () => void
  onDistributeCredits: () => void
  onAdjustUnusedDistributedCredits: () => void
  onBatchDistributeCredit: () => void
  onCreateCorporateDistributionScheduleModal: () => void
}
const CorporateDescriptions = (props: Iprops) => {
  const {
    corporate,
    corporateClientCredential,
    onEditCorporateClientCredential,
    onDeleteCorporateClientCredential,
    onAdjustmentCredits,
    onDistributeCredits,
    onAdjustUnusedDistributedCredits,
    onBatchDistributeCredit,
    onCreateCorporateDistributionScheduleModal,
  } = props
  const navigate = useNavigate()
  const userPermissions: string[] = useSelector(
    (state: any) =>
      state.userInfo?.permissions?.map((permission: { scope: string }) => permission.scope) ?? [],
  )
  const canDistributeCredits = userPermissions.includes(PermissionsEnum.CORPORATE_MEMBERS_WRITE)
  const canCreateDistributionSchedule = userPermissions.includes(
    PermissionsEnum.CORPORATE_CREDITS_DISTRIBUTION_SCHEDULES_WRITE,
  )
  const [disabledCorporateCreditsHistoriesButton, setDisabledCorporateCreditsHistoriesButton] =
    useState<boolean>(false)
  const [activityTags, setActivityTags] = useState<ITag[]>([])

  const location = useLocation()

  if (corporate.corporate.contractDeadline) {
    corporate.corporate.contractDeadline = dayjs(corporate.corporate.contractDeadline)
  }
  if (corporate.corporate.nextDistributionTime) {
    corporate.corporate.nextDistributionTime = dayjs(
      corporate.corporate.nextDistributionTime,
      'YYYY-MM-DDTHH:mm:ss',
    )
  }

  useEffect(() => {
    if (location.pathname.match(/^\/corporates\/[0-9]+\/credits\/histories$/)) {
      setDisabledCorporateCreditsHistoriesButton(true)
    } else {
      setDisabledCorporateCreditsHistoriesButton(false)
    }

    getTagsByContext(TaggingContextEnums.ACTIVITIES).then(res => {
      if (res) {
        setActivityTags(Object.values(res).flat())
      }
    })
  }, [location.pathname])

  useEffect(() => {
    if (
      corporate.corporate.disallowStudioIds?.length &&
      corporate.corporate.disallowStudioIds.length <= MAX_QS_STRINGIFY_LENGTH
    ) {
      getStudiosSuggestionsByIds(corporate.corporate.disallowStudioIds).then(res => {
        setStudioState(res)
      })
    }
    if (
      corporate.corporate.disallowCityList?.length &&
      corporate.corporate.disallowCityList.length <= MAX_QS_STRINGIFY_LENGTH
    ) {
      getCities().then(res => {
        const data = res as any as ICity[]
        setCitiesState(
          data
            .filter(d => corporate.corporate.disallowCityList?.includes(d.id))
            .map(d => ({
              id: d.id,
              name: d.translations.find(t => t.locale === 'zh-CN')?.name || '',
            })),
        )
      })
    }
  }, [corporate])

  const toEdit = () => {
    navigate(`/corporates/${corporate?.corporate.id}/edit`)
  }
  const [studiosState, setStudioState] = useState<{ id: number; name: string }[]>([])
  const [citiesState, setCitiesState] = useState<{ id: number; name: string }[]>([])

  const distributionCreditsMenuItems = [
    {
      key: '1',
      label: (
        <Button type="link" icon={<SolutionOutlined />}>
          <FormattedMessage id="corporate.credits.distributeToAll" />
        </Button>
      ),
      onClick: () => {
        confirm({
          title:
            corporate && corporate.report.activatedMemberCount > 0
              ? translate('corporate.credits.distributeToAll.confirm', {
                  activatedMemberCount: corporate.report.activatedMemberCount,
                })
              : translate('corporate.credits.distributeToAll.confirmNoMember'),
          icon: <ExclamationCircleOutlined />,
          onOk() {
            if (corporate && corporate.corporate.id && corporate.report.activatedMemberCount > 0) {
              onDistributeCredits()
            }
          },
        })
      },
    },
    {
      key: '2',
      label: (
        <Button type="link" icon={<CloudUploadOutlined />}>
          <FormattedMessage id="corporate.credits.distributeToUploaded" />
        </Button>
      ),
      onClick: () => {
        onBatchDistributeCredit()
      },
    },
    {
      key: '3',
      label: (
        <Button type="link" icon={<ToolOutlined />}>
          <FormattedMessage id="navbar.corporateCreditsDistributionSchedule.create" />
        </Button>
      ),
      onClick: () => {
        onCreateCorporateDistributionScheduleModal()
      },
    },
  ].filter(item => (item.key === '3' ? canCreateDistributionSchedule : canDistributeCredits))

  return (
    <>
      <Space orientation="vertical" style={{ width: '100%' }} size={16}>
        <Descriptions
          title="合作企业详情"
          styles={{
            label: { alignItems: 'center', fontWeight: 600, width: '200px' },
            content: { minWidth: 300 },
          }}
          column={2}
          bordered
          extra={
            <Space>
              {distributionCreditsMenuItems.length > 0 && (
                <Dropdown
                  menu={{
                    items: distributionCreditsMenuItems,
                  }}
                >
                  <a>
                    {translate('corporate.credits.distribution')} <DownOutlined />
                  </a>
                </Dropdown>
              )}

              <AuthPermission permission={PermissionsEnum.CORPORATES_WRITE}>
                <Button onClick={toEdit} type="primary">
                  {translate('edit')}
                </Button>
              </AuthPermission>
            </Space>
          }
        >
          <Descriptions.Item label="ID">{corporate?.corporate.id}</Descriptions.Item>
          <Descriptions.Item label={translate('name')}>
            {corporate?.corporate.name}
          </Descriptions.Item>
          <Descriptions.Item label={translate('corporate.unusedDistributedCreditsThreshold')}>
            {corporate?.corporate.unusedDistributedCreditsThreshold}
          </Descriptions.Item>
          <Descriptions.Item label={translate('isActive')}>
            <YesOrNoTag value={corporate?.corporate.isActive} />
          </Descriptions.Item>
          <Descriptions.Item label="是否试用企业">
            <YesOrNoTag
              value={corporate?.corporate.isTrial}
              yesColor={'yellow'}
              noColor={'green'}
            />
          </Descriptions.Item>
          <Descriptions.Item label="是否允许对用户显示点数池">
            <YesOrNoTag value={corporate?.corporate.allowShowCreditsPool} />
          </Descriptions.Item>
          <Descriptions.Item label={translate('corporate.enableReclaimCancelBooking')}>
            <YesOrNoTag value={corporate?.corporate.enableReclaimCancelBooking} />
          </Descriptions.Item>
          <Descriptions.Item label={translate('corporate.principal')}>
            {corporate?.corporate.principal}
          </Descriptions.Item>
          <Descriptions.Item label={translate('corporate.cooperationMode')}>
            {corporate?.corporate.cooperationMode}
          </Descriptions.Item>
          <Descriptions.Item label={translate('corporate.contractDeadline')}>
            {corporate?.corporate.contractDeadline?.format('YYYY-MM-DD')}
          </Descriptions.Item>
          <Descriptions.Item label={translate('corporate.distributionFrequency')}>
            {corporate?.corporate.distributionFrequency}
          </Descriptions.Item>
          <Descriptions.Item label={translate('corporate.nextDistributionTime')}>
            {corporate?.corporate.nextDistributionTime?.format('YYYY-MM-DD HH:mm:ss')}
          </Descriptions.Item>
          <Descriptions.Item label={translate('corporate.distributionRuleRemark')}>
            {corporate?.corporate.distributionRuleRemark}
          </Descriptions.Item>
          <Descriptions.Item label={translate('corporate.price')}>
            {corporate.corporate.price === null
              ? '-'
              : `${(corporate.corporate.price / 100).toFixed(2)} 元/点`}
          </Descriptions.Item>
          <Descriptions.Item label={translate('createdAt')}>
            {getDateTime(corporate?.corporate.createdAt)}
          </Descriptions.Item>
          <Descriptions.Item label={translate('updatedAt')}>
            {getDateTime(corporate?.corporate.updatedAt)}
          </Descriptions.Item>
        </Descriptions>

        <Descriptions
          title="企业客户端凭证"
          styles={{
            label: { alignItems: 'center', fontWeight: 600, width: '200px' },
            content: { minWidth: 300 },
          }}
          column={2}
          bordered
          extra={
            <Space>
              <AuthPermission permission={PermissionsEnum.CORPORATES_WRITE}>
                <Button type="primary" onClick={onEditCorporateClientCredential}>
                  {translate('edit')}
                </Button>
              </AuthPermission>

              {corporateClientCredential && (
                <AuthPermission permission={PermissionsEnum.CORPORATES_WRITE}>
                  <Button danger onClick={onDeleteCorporateClientCredential}>
                    {translate('delete')}
                  </Button>
                </AuthPermission>
              )}
            </Space>
          }
        >
          <Descriptions.Item
            label={translate('corporate.corporateClientCredential.clientCredential')}
          >
            {corporateClientCredential?.clientCredential.name || '未设置'}
          </Descriptions.Item>
          <Descriptions.Item
            label={translate('corporate.corporateClientCredential.maxCreditsExpiredAt')}
          >
            {corporateClientCredential?.maxCreditsExpiredAt
              ? getDateTime(corporateClientCredential.maxCreditsExpiredAt)
              : '未设置'}
          </Descriptions.Item>
        </Descriptions>

        <Descriptions
          title="企业点数"
          styles={{
            label: { alignItems: 'center', fontWeight: 600, width: '200px' },
            content: { minWidth: 300 },
          }}
          column={2}
          bordered
          extra={
            <Space>
              <Button
                disabled={disabledCorporateCreditsHistoriesButton}
                type="link"
                className={styles['view-credits-history']}
                href={`/corporates/${corporate?.corporate.id}/credits/histories`}
              >
                {translate('corporate.credits.viewDetails')}
              </Button>

              <AuthPermission permission={PermissionsEnum.CORPORATE_CREDITS_ADJUSTMENT_WRITE}>
                <Button type="primary" onClick={onAdjustmentCredits}>
                  {translate('corporate.credits.adjustment')}
                </Button>
              </AuthPermission>

              <AuthPermission permission={PermissionsEnum.CORPORATES_WRITE}>
                <Button type="primary" onClick={onAdjustUnusedDistributedCredits}>
                  {translate('corporate.credits.adjustUnusedDistributedCredits')}
                </Button>
              </AuthPermission>
            </Space>
          }
        >
          <Descriptions.Item label="点数">{corporate?.corporate.credits}</Descriptions.Item>
          <Descriptions.Item label="分发的点数（含buffer）">
            {corporate?.report.distributedCredits}
          </Descriptions.Item>
          <Descriptions.Item label={translate('corporate.unusedDistributedCredits')}>
            {corporate?.corporate.unusedDistributedCredits}
          </Descriptions.Item>
          <Descriptions.Item label="成员已使用的点数">
            {corporate?.report.memberUsedCredits}
          </Descriptions.Item>
          <Descriptions.Item label="成员账户显示未使用的点数（含buffer）">
            {corporate?.report.memberUnusedCredits}
          </Descriptions.Item>
          <Descriptions.Item label="所有成员数">
            {corporate?.report.allMemberCount}
          </Descriptions.Item>
          <Descriptions.Item label="当前激活成员数">
            {corporate?.report.activatedMemberCount}
          </Descriptions.Item>
        </Descriptions>

        <Descriptions
          title="企业预约限制"
          styles={{
            label: { alignItems: 'center', fontWeight: 600, width: '200px' },
            content: { minWidth: 300 },
          }}
          column={2}
          bordered
        >
          <Descriptions.Item label="不允许预约的场馆列表">
            {corporate.corporate.disallowStudioIds &&
              corporate.corporate.disallowStudioIds?.length > 100 &&
              corporate.corporate.disallowStudioIds?.map(id => {
                return (
                  <Tag color="blue">
                    <Button
                      type="link"
                      target="_black"
                      href={`${guavapassUrl}/admin/studios/${id}`}
                    >
                      {id}
                    </Button>
                  </Tag>
                )
              })}
            {studiosState?.map(studio => {
              return (
                <Tag color="blue" key={studio.id}>
                  <Button
                    type="link"
                    target="_black"
                    href={`${guavapassUrl}/admin/studios/${studio.id}`}
                  >
                    {studio.name}
                  </Button>
                </Tag>
              )
            })}
          </Descriptions.Item>
          <Descriptions.Item label="不允许预约的activities">
            {corporate.corporate.disallowActivityList?.map(id => {
              const tag = activityTags.find(item => item.id === id)
              if (tag) {
                const tagName = tag.tagTranslations.find(t => t.locale === 'zh-CN')
                const tagCategoryAndSubCategory = [tag.category, tag.subCategory].join('/')
                const tagDisplayName = `${tagName?.name} (${tagCategoryAndSubCategory})`
                return <Tag key={id}>{tagDisplayName}</Tag>
              }
            })}
          </Descriptions.Item>
          <Descriptions.Item label="单次预约允许使用最大点数">
            {corporate.corporate.maxCreditsUsageThreshold || '无限制'}
          </Descriptions.Item>
          <Descriptions.Item label="是否允许超过单次最大使用点数后使用混合支付">
            <YesOrNoTag value={corporate.corporate.enabledMixPayment} />
          </Descriptions.Item>
          <Descriptions.Item label="不允许的城市列表">
            {citiesState.map(city => {
              return <Tag key={city.id}>{city.name}</Tag>
            })}
          </Descriptions.Item>
        </Descriptions>
      </Space>
    </>
  )
}

export default CorporateDescriptions
